/**
 * Full-page booking form (to-fill). Opened from My Bookings when user clicks a "to fill" card.
 * Submitted forms are shown in sheet only from JobsPage.
 * iOS-first UI with project-consistent Checkbox, Input, and section cards.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Plus, ChevronDown, ChevronUp, Trash2, User, Calendar, CreditCard, FileText, Banknote, Phone, Mail, Percent, Wallet, CalendarClock } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../api/client';
import { API_ENDPOINTS } from '../../../api/endpoints';
import type {
  BookingFormItem,
  BookingFormSubmitPayload,
} from '../JobsPage';
import { usePerformanceDetailsStore } from '../../../stores/performanceDetailsStore';
import { useTechRiderTblStore } from '../../../stores/techRiderTblStore';
import { useBillingStore } from '../../../stores/billingStore';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Input } from '../../../components/ui/Input';
import { Loader } from '@/components/ui';
import { AnnexureAContent } from '@/components/booking/AnnexureAContent';
import { cn } from '@/utils/cn';
import type { PerformanceMemberItem } from '@/services/artistService';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import { useBookingFormsStore } from '@/stores/bookingFormsStore';

function formatBookingEventDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const mon = d.toLocaleDateString('en-IN', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${mon} ${year}`;
}

function formatBookingINR(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `₹${n.toLocaleString('en-IN')}`;
}

function removeSecondsFromTime(timeStr: string | null | undefined): string {
  if (!timeStr) return '—';
  const match = timeStr.match(/^(\d{1,2}:\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (match) return `${match[1]} ${(match[2] ?? '').toUpperCase()}`.trim();
  return timeStr;
}

function formatPaymentTerms(terms: string | null | undefined): string {
  if (!terms) return '—';
  const t = String(terms).trim();
  const n = parseInt(t, 10);
  if (!Number.isNaN(n) && String(n) === t) return `Within ${n} day${n !== 1 ? 's' : ''}`;
  return t;
}

const REGEX_GST = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const REGEX_IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const REGEX_ACCOUNT = /^\d{9,18}$/;

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'others', label: 'Others' },
] as const;

type MemberOption = { id: string; name: string; role: string; age: number };
type DraftMemberRow = { id: string; name: string; role: string; age: string; gender: string };
type BookingFillDraft = {
  selectedMemberIds: string[];
  techRiderValue: string;
  travelBoardingLodging: string;
  selectedBankId: number | null;
  gstin: string;
  pan: string;
};

const TERMS_TEXT = `I/we have read and understood the Booking Terms and Conditions as set out in Annexure A and titled "Booking Terms and Conditions". My performance at the Event shall be subject to the compliance, by me/us, with the terms and conditions, restrictions and limitations contained herein and all laws, notifications and rules as may be applicable including any amendment or variation thereof. I/we have read and understood, and have hereby agreed to abide by all such terms and conditions, restrictions and limitations. I/we do hereby declare that my/our booking for the Event is irrevocable and the particulars/information given by me/us are true & correct and nothing has been concealed there from. In case of any information being found to be false, partly or in whole, StarClinch shall have the unconditional right to cancel/reject my/our booking and forfeit the amount to be paid to me/us in terms of this booking.`;

export const BookingFillPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const formFromState = (location.state as { form?: BookingFormItem })?.form;

  const [form, setForm] = useState<BookingFormItem | null>(formFromState ?? null);
  const [formLoading, setFormLoading] = useState(!formFromState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [annexureOpen, setAnnexureOpen] = useState(false);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [draftNewMembers, setDraftNewMembers] = useState<DraftMemberRow[]>(() => [
    { id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' },
  ]);
  const [addMemberErrors, setAddMemberErrors] = useState<Record<string, string>>({});

  const performanceData = usePerformanceDetailsStore((s) => s.data);
  const ensurePerformanceFetched = usePerformanceDetailsStore((s) => s.ensureFetched);
  const updatePerformanceDetails = usePerformanceDetailsStore((s) => s.updatePerformanceDetails);
  const saveLoadingPerformance = usePerformanceDetailsStore((s) => s.saveLoading);
  const techRiderTblData = useTechRiderTblStore((s) => s.data);
  const ensureTechRiderTblFetched = useTechRiderTblStore((s) => s.ensureFetched);
  const billingData = useBillingStore((s) => s.data);
  const ensureBillingFetched = useBillingStore((s) => s.ensureFetched);

  const membersCount = form?.members_count ?? 0;
  const techRiderEnabled = String(form?.tech_rider ?? '') === '1';
  const tblEnabled = Number(form?.tbl_needed) === 1;

  const profileMembers: MemberOption[] = (performanceData?.performing_members ?? []).map(
    (m: PerformanceMemberItem, i: number) => ({
      id: `profile-${m.id ?? i}`,
      name: m.name ?? '',
      role: m.role ?? '',
      age: m.age ?? 0,
    })
  );
  const allOptions: MemberOption[] = profileMembers;
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());

  // When performing_members list updates (e.g. after adding a member), option ids can change
  // (e.g. temporary profile-1 becomes profile-201 after API returns). Prune stale selected ids
  // so we don't block selecting the newly added member with "only 2 members" toast.
  useEffect(() => {
    const validIds = new Set(allOptions.map((o) => o.id));
    setSelectedMemberIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [performanceData?.performing_members]);

  const [techRiderValue, setTechRiderValue] = useState('');
  const [travelBoardingLodging, setTravelBoardingLodging] = useState('');
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [draftHydrated, setDraftHydrated] = useState(false);

  const bankFieldsFromSelection = selectedBankId != null
    ? billingData?.banking_details?.find((b) => b.id === selectedBankId)
    : null;

  const bank_name = bankFieldsFromSelection?.bank_name ?? '';
  const branch_name = bankFieldsFromSelection?.branch ?? '';
  const ifsc_code = bankFieldsFromSelection?.ifsc_code ?? '';
  const account_number = bankFieldsFromSelection?.account_number ?? '';
  const account_name = bankFieldsFromSelection?.account_holder_name ?? '';
  const account_type = bankFieldsFromSelection?.account_type === 'current' ? 'Current' : 'Savings';

  const draftKey = form?.id != null ? STORAGE_KEYS.BOOKING_FILL_DRAFT(form.id) : null;

  useEffect(() => {
    if (!formId) return;
    if (formFromState && Number(formId) === formFromState.id) {
      setForm(formFromState);
      setFormLoading(false);
      return;
    }
    setFormLoading(true);
    apiClient
      .get<{ success: boolean; data: BookingFormItem[] }>(API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORMS)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data?.data)) {
          const found = res.data.data.find((f) => f.id === Number(formId));
          setForm(found ?? null);
        } else {
          setForm(null);
        }
      })
      .catch(() => setForm(null))
      .finally(() => setFormLoading(false));
  }, [formId, formFromState]);

  useEffect(() => {
    if (!formLoading && formId && form === null) {
      navigate('/jobs', { state: { tab: 'express' } });
    }
  }, [formLoading, formId, form, navigate]);

  useEffect(() => {
    ensurePerformanceFetched();
    ensureTechRiderTblFetched();
    ensureBillingFetched();
  }, [ensurePerformanceFetched, ensureTechRiderTblFetched, ensureBillingFetched]);

  useEffect(() => {
    if (!form) return;
    setTechRiderValue(form.tech_rider_value ?? '');
    setTravelBoardingLodging(form.travel_boarding_lodging ?? '');
    setGstin((form.gstin ?? '').trim());
    setPan((form.pan ?? '').trim());
    const banks = billingData?.banking_details ?? [];
    const match = banks.find(
      (b) =>
        b.account_number === form.account_number &&
        b.ifsc_code === form.ifsc_code
    );
    setSelectedBankId(match?.id ?? null);
  }, [form?.id]);

  useEffect(() => {
    if (!form?.id || !draftKey) return;
    if (typeof sessionStorage === 'undefined') {
      setDraftHydrated(true);
      return;
    }
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (!raw) {
        setDraftHydrated(true);
        return;
      }
      const parsed = JSON.parse(raw) as Partial<BookingFillDraft>;
      if (Array.isArray(parsed.selectedMemberIds)) {
        setSelectedMemberIds(new Set(parsed.selectedMemberIds.filter((id) => typeof id === 'string')));
      }
      if (typeof parsed.techRiderValue === 'string') setTechRiderValue(parsed.techRiderValue);
      if (typeof parsed.travelBoardingLodging === 'string') setTravelBoardingLodging(parsed.travelBoardingLodging);
      if (typeof parsed.selectedBankId === 'number') setSelectedBankId(parsed.selectedBankId);
      if (parsed.selectedBankId === null) setSelectedBankId(null);
      if (typeof parsed.gstin === 'string') setGstin(parsed.gstin);
      if (typeof parsed.pan === 'string') setPan(parsed.pan);
    } catch {
      // Ignore malformed draft and continue with current in-memory values.
    } finally {
      setDraftHydrated(true);
    }
  }, [form?.id, draftKey]);

  useEffect(() => {
    if (!draftHydrated || !draftKey) return;
    if (typeof sessionStorage === 'undefined') return;
    const draft: BookingFillDraft = {
      selectedMemberIds: Array.from(selectedMemberIds),
      techRiderValue,
      travelBoardingLodging,
      selectedBankId,
      gstin,
      pan,
    };
    sessionStorage.setItem(draftKey, JSON.stringify(draft));
  }, [
    draftHydrated,
    draftKey,
    selectedMemberIds,
    techRiderValue,
    travelBoardingLodging,
    selectedBankId,
    gstin,
    pan,
  ]);

  useEffect(() => {
    if (!techRiderTblData || !form) return;
    if (techRiderEnabled) {
      if (techRiderTblData.tech_rider && (techRiderTblData.tech_rider_desc ?? '').trim()) {
        setTechRiderValue(techRiderTblData.tech_rider_desc ?? '');
      } else {
        setTechRiderValue('');
      }
    }
    if (tblEnabled) {
      if (techRiderTblData.tbl && (techRiderTblData.tbl_desc ?? '').trim()) {
        setTravelBoardingLodging(techRiderTblData.tbl_desc ?? '');
      } else {
        setTravelBoardingLodging('');
      }
    }
  }, [form?.id, techRiderEnabled, tblEnabled, techRiderTblData?.tech_rider, techRiderTblData?.tech_rider_desc, techRiderTblData?.tbl, techRiderTblData?.tbl_desc]);

  useEffect(() => {
    if (!billingData?.gst_details) return;
    setGstin(billingData.gst_details.gstin ?? '');
    setPan(billingData.gst_details.pan ?? '');
  }, [billingData?.gst_details?.gstin, billingData?.gst_details?.pan]);

  const handleToggleMember = (id: string, checked: boolean) => {
    setValidationError(null);
    if (checked) {
      setSelectedMemberIds((prev) => {
        if (prev.size >= membersCount) {
          toast.error(`You can select only ${membersCount} performing member(s). Uncheck one to select another.`);
          return prev;
        }
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    } else {
      setSelectedMemberIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleOpenAddMember = () => {
    setShowAddMemberForm(true);
    setDraftNewMembers([{ id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' }]);
    setAddMemberErrors({});
  };

  const handleAddMoreDraftMembers = () => {
    setDraftNewMembers((prev) => [
      ...prev,
      { id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: '', role: '', age: '', gender: '' },
    ]);
  };

  const handleRemoveDraftMember = (id: string) => {
    setDraftNewMembers((prev) => (prev.length <= 1 ? prev : prev.filter((m) => m.id !== id)));
  };

  const handleDraftMemberChange = (id: string, field: keyof Omit<DraftMemberRow, 'id'>, value: string) => {
    setDraftNewMembers((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  };

  const handleSaveAddMembers = async () => {
    const newErrors: Record<string, string> = {};
    draftNewMembers.forEach((member) => {
      const prefix = `draft-${member.id}`;
      if (!member.name?.trim()) newErrors[`${prefix}-name`] = 'Name required';
      if (!member.role?.trim()) newErrors[`${prefix}-role`] = 'Role required';
      if (!member.age?.trim()) newErrors[`${prefix}-age`] = 'Age required';
      if (!member.gender) newErrors[`${prefix}-gender`] = 'Gender required';
    });
    setAddMemberErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      toast.error(newErrors[firstKey]);
      return;
    }
    const existing = performanceData?.performing_members ?? [];
    const existingPayload = existing.map((m) => ({
      name: m.name ?? '',
      role: m.role ?? '',
      age: Number(m.age ?? 0),
      gender_slug: typeof m.gender === 'string' ? m.gender : (m.gender as { g_slug?: string })?.g_slug ?? '',
    }));
    const newPayload = draftNewMembers.map((m) => ({
      name: m.name.trim(),
      role: m.role.trim(),
      age: Number(m.age) || 0,
      gender_slug: m.gender,
    }));
    try {
      await updatePerformanceDetails({
        performing_members: [...existingPayload, ...newPayload],
      });
      toast.success('Members added. Select them below.');
      setShowAddMemberForm(false);
      setDraftNewMembers([{ id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' }]);
      setAddMemberErrors({});
    } catch {
      // Store already toasts error
    }
  };

  const handleCancelAddMembers = () => {
    setShowAddMemberForm(false);
    setDraftNewMembers([{ id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' }]);
    setAddMemberErrors({});
  };

  const validate = (): string | null => {
    if (!form) return 'Form not loaded.';
    const selected = allOptions.filter((m) => selectedMemberIds.has(m.id));
    if (selected.length < membersCount) {
      return `Please select ${membersCount} performing member(s). You can add members below if needed.`;
    }
    if (techRiderEnabled && !techRiderValue.trim()) return 'Tech rider details are required.';
    if (tblEnabled && !travelBoardingLodging.trim()) return 'Travel / boarding / lodging details are required.';
    if (selectedBankId == null) return 'Please select a bank account.';
    const bank = billingData?.banking_details?.find((b) => b.id === selectedBankId);
    if (!bank) return 'Please select a bank account.';
    if (!REGEX_IFSC.test(bank.ifsc_code.trim())) return 'Invalid IFSC code on selected account.';
    if (!REGEX_ACCOUNT.test(bank.account_number.trim())) return 'Invalid account number on selected account.';
    if (gstin.trim() && !REGEX_GST.test(gstin.trim())) return 'Enter a valid GSTIN.';
    if (!pan.trim()) return 'PAN number is required.';
    if (!termsAccepted) return 'You must accept the terms and conditions to proceed.';
    return null;
  };

  const handleConfirmBookingClick = () => {
    const err = validate();
    setValidationError(err ?? null);
    if (err) {
      toast.error(err);
      return;
    }
    setConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!form) return;
    const err = validate();
    if (err) {
      setValidationError(err);
      toast.error(err);
      setConfirmModalOpen(false);
      return;
    }
    setValidationError(null);
    const selected = allOptions.filter((m) => selectedMemberIds.has(m.id));
    const members_info = JSON.stringify(
      selected.map((m) => ({ name: m.name, role: m.role, age: m.age }))
    );
    const payload: BookingFormSubmitPayload = {
      members_info,
      tech_rider_value: techRiderEnabled ? techRiderValue.trim() : '',
      travel_boarding_lodging: tblEnabled ? travelBoardingLodging.trim() : '',
      gstin: gstin.trim(),
      pan: pan.trim(),
      bank_name,
      branch_name,
      ifsc_code,
      account_number,
      account_name,
      account_type: account_type === 'Current' ? 'Current' : 'Savings',
      feedback: '',
      rating: 0,
    };
    setSubmitLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORM_SUBMIT(form.id), payload);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(STORAGE_KEYS.BOOKING_FILL_DRAFT(form.id));
      }
      toast.success('Booking confirmed successfully.');
      setConfirmModalOpen(false);
      await useBookingFormsStore.getState().fetchBookingForms();
      navigate('/jobs', { state: { tab: 'express' } });
    } catch {
      const msg = 'Something went wrong. Please try again.';
      setValidationError(msg);
      toast.error(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (formLoading || !form) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <Loader size="small" />
      </div>
    );
  }

  const banks = billingData?.banking_details ?? [];
  const gstDetails = billingData?.gst_details;
  const hasPan = (gstDetails?.pan ?? '').trim().length > 0;

  const inputBaseClass = 'w-full rounded-[0.75rem] bg-transparent px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] border border-[#3A3A3C] min-h-[44px]';

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white pb-28">
      {/* Header — minimal */}
      <header className="sticky top-5 z-10 bg-[#0B0B0D]/90 backdrop-blur-md border-b border-[#3A3A3C] safe-area-inset-top">
        <div className="flex items-center gap-3 px-4 h-14">
          <button
            type="button"
            onClick={() => navigate('/jobs', { state: { tab: 'express' } })}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2 rounded-xl text-[#8E8E93] hover:text-white hover:bg-[#1C1C1E] active:scale-[0.98] transition-colors"
            aria-label="Back to My Bookings"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex flex-col min-w-0">
            <h1 className="text-[17px] font-semibold text-white truncate">{form.artist_act}</h1>
            <p className="text-[13px] text-[#8E8E93] truncate">{form.event_type} · #{form.id}</p>
          </div>
        </div>
      </header>

      <div className="md:px-4 py-10  mx-auto space-y-6">
        {/* Inline validation error — no toasts */}
        {validationError && (
          <div
            role="alert"
            className="flex items-start gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
          >
            <span className="shrink-0 mt-0.5">!</span>
            <p className="flex-1">{validationError}</p>
            <button type="button" onClick={() => setValidationError(null)} className="shrink-0 text-red-400/80 hover:text-red-400">Dismiss</button>
          </div>
        )}

        {/* Contact & event — minimal; on md+ Contact | When & where in row, Fee & payment right below When & where */}
        <section className="px-1 py-3 text-[13px] grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-x-6">
          <div className="space-y-1.5">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">Contact</p>
            <p className="text-white/90 font-medium flex items-center gap-2">
              <User className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.contact_name}
            </p>
            <p className="text-[#8E8E93] flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.contact_phone}
            </p>
            <p className="text-[#8E8E93] flex items-center gap-2 truncate">
              <Mail className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.contact_email}
            </p>
          </div>
          <div className="space-y-1.5 pt-2 md:pt-0 border-t border-[#3A3A3C]/50 md:border-t-0">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">When & where</p>
            <p className="text-[#8E8E93] flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {formatBookingEventDate(form.event_date)} · Report {removeSecondsFromTime(form.report_at)} · Start {removeSecondsFromTime(form.event_start_at)}
              {form.performance_duration_mins != null && ` · ${form.performance_duration_mins} min`}
            </p>
            <p className="text-white/90 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
              {form.venue}{form.venue_city_state && form.venue_city_state !== form.venue ? ` · ${form.venue_city_state}` : ''}
            </p>
            {form.google_map_link && (
              <a href={form.google_map_link} target="_blank" rel="noopener noreferrer" className="bg-[#F33363]/90 hover:bg-[#F33363]/70 text-white hover:text-white/80 font-medium text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 w-fit">
                <MapPin className="h-3.5 w-3.5" /> Map
              </a>
            )}
          </div>

          <div className="space-y-1.5 pt-4 border-t border-[#3A3A3C]/50 block md:invisible">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">Fee & payment</p>
            <div className='md:col-start-2 flex flex-col space-y-1.5 md:space-y-1'>
              <div className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <Banknote className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <span>Artist Fee</span>
                  {form.taf != null && String(form.taf).trim() !== '' && (
                    <span className="text-[#FFD60A] text-xs">
                      ({`${String(form.taf).replace(/\.00$/, '')}% TAF Included`})
                    </span>
                  )}
                </div>

                {formatBookingINR(form.fee)}
              </div>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <Percent className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>Advance Amount {form.advance_percentage != null && form.advance_percentage !== '' ? `(${String(form.advance_percentage).replace(/\.00$/, '')}%)` : '—'}</p>
                </div>
                {formatBookingINR(form.advance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <Wallet className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>Balance </p>
                </div>
                {formatBookingINR(form.balance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <CalendarClock className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>Payment Terms </p>
                </div>
                {formatPaymentTerms(form.payment_terms)}
              </p>
            </div>
          </div>
          <div className="space-y-1.5 pt-4 border-t border-[#3A3A3C]/50 hidden md:block">
            <p className="text-[11px] font-medium text-[#6E6E73] uppercase tracking-wider">Fee & payment</p>
            <div className='md:col-start-2 flex flex-col space-y-1.5 md:space-y-1'>
              <div className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <Banknote className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <span>Artist Fee</span>
                  {form.taf != null && String(form.taf).trim() !== '' && (
                    <span className="text-[#FFD60A] text-xs">
                      ({`${String(form.taf).replace(/\.00$/, '')}% TAF Included`})
                    </span>
                  )}
                </div>

                {formatBookingINR(form.fee)}
              </div>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <Percent className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>Advance Amount {form.advance_percentage != null && form.advance_percentage !== '' ? `(${String(form.advance_percentage).replace(/\.00$/, '')}%)` : '—'}</p>
                </div>
                {formatBookingINR(form.advance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <Wallet className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>Balance </p>
                </div>
                {formatBookingINR(form.balance_amount)}
              </p>
              <p className="text-[#8E8E93] flex items-center gap-2 justify-between">
                <div className='flex flex-row items-center gap-2'>
                  <CalendarClock className="h-3.5 w-3.5 shrink-0 text-[#6E6E73]" />
                  <p>Payment Terms </p>
                </div>
                {formatPaymentTerms(form.payment_terms)}
              </p>
            </div>
          </div>
        </section>

        {/* Performing members */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <User className="h-4 w-4" /> Performing members
            </h2>
            <p className="text-white/80 text-sm mt-1">Select up to {membersCount} from your profile. Add members below to save to profile, then select here.</p>
          </div>
          <div className="p-5 space-y-3">
            {allOptions.length === 0 ? (
              <p className="text-[#8E8E93] text-sm py-2">No members in your profile yet. Add one below.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {allOptions.map((m) => (
                  <li
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleToggleMember(m.id, !selectedMemberIds.has(m.id))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleMember(m.id, !selectedMemberIds.has(m.id));
                      }
                    }}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-xl border min-h-[52px] cursor-pointer transition-colors',
                      selectedMemberIds.has(m.id) ? 'border-[#F33363]/40 bg-[#F33363]/5' : 'border-[#2E2E32] bg-[#1C1C1E]/80'
                    )}
                  >
                    <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                      <Checkbox
                        checked={selectedMemberIds.has(m.id)}
                        onCheckedChange={(c) => handleToggleMember(m.id, c === true)}
                      />
                    </div>
                    <div className="flex-1 items-center gap-2 min-w-0">
                      <p className="text-white font-medium truncate">{m.name}</p>
                      <p className="text-[#8E8E93] text-xs truncate">{m.role}{m.age ? ` · ${m.age}` : ''}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {membersCount > 0 && allOptions.length < membersCount && (
              <p className="text-amber-500/90 text-[13px]">Add {membersCount - allOptions.length} more member(s) below.</p>
            )}
            {!showAddMemberForm && (
              <button
                type="button"
                onClick={handleOpenAddMember}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-[#2E2E32] text-[#8E8E93] hover:text-white hover:border-[#4A4A4E] hover:bg-[#1C1C1E]/80 transition-colors min-h-[44px]"
              >
                <Plus className="h-4 w-4" /> Add member (saves to profile)
              </button>
            )}
            {showAddMemberForm && (
              <div className="p-4 rounded-2xl bg-[#1C1C1E] border border-[#2E2E32] space-y-4">
                <button
                  type="button"
                  onClick={handleAddMoreDraftMembers}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" /> Add more members
                </button>
                {draftNewMembers.map((member, index) => {
                  const prefix = `draft-${member.id}`;
                  return (
                    <div key={member.id} className="rounded-xl border border-[#2E2E32] p-3 space-y-3 bg-[#121214]">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-[#8E8E93]">New member {index + 1}</span>
                        {draftNewMembers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveDraftMember(member.id)}
                            className="p-1.5 rounded-lg text-[#8E8E93] hover:text-red-400 hover:bg-red-500/10"
                            aria-label="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <Input label="Name" value={member.name} onChange={(e) => handleDraftMemberChange(member.id, 'name', e.target.value)} placeholder="Full name" className={inputBaseClass} error={addMemberErrors[`${prefix}-name`]} />
                      <Input label="Role" value={member.role} onChange={(e) => handleDraftMemberChange(member.id, 'role', e.target.value)} placeholder="e.g. Vocalist" className={inputBaseClass} error={addMemberErrors[`${prefix}-role`]} />
                      <div className="grid grid-cols-2 gap-3">
                        <Input type="number" label="Age" value={member.age} onChange={(e) => handleDraftMemberChange(member.id, 'age', e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="0" inputMode="numeric" className={inputBaseClass} error={addMemberErrors[`${prefix}-age`]} />
                        <div>
                          <label className="block text-xs font-medium text-[#8E8E93] mb-1">Gender</label>
                          <select
                            value={member.gender}
                            onChange={(e) => handleDraftMemberChange(member.id, 'gender', e.target.value)}
                            className={inputBaseClass}
                          >
                            <option value="">Select</option>
                            {GENDER_OPTIONS.map((g) => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </select>
                          {addMemberErrors[`${prefix}-gender`] && <p className="text-red-400 text-xs mt-1">{addMemberErrors[`${prefix}-gender`]}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="flex gap-2 pt-1">
                  <Button type="button" variant="active" onClick={() => void handleSaveAddMembers()} className="flex-1 min-h-[44px]" disabled={saveLoadingPerformance}>
                    {saveLoadingPerformance ? <Loader className="h-5 w-5 animate-spin" /> : 'Save to profile'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleCancelAddMembers} className="flex-1 min-h-[44px]" disabled={saveLoadingPerformance}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Tech rider & TBL */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <FileText className="h-4 w-4" /> Tech rider & travel
            </h2>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <p className="text-[#8E8E93] text-xs font-medium mb-1">Tech rider</p>
              {techRiderEnabled ? (
                <>
                  <p className="text-emerald-500/90 text-[13px] font-medium mb-2">Applicable</p>
                  <textarea value={techRiderValue} onChange={(e) => { setTechRiderValue(e.target.value); setValidationError(null); }} placeholder="Enter tech rider details" className={cn(inputBaseClass, 'min-h-[100px] resize-y py-3')} rows={3} />
                </>
              ) : (
                <p className="text-[#8E8E93] text-sm">Not applicable</p>
              )}
            </div>
            <div>
              <p className="text-[#8E8E93] text-xs font-medium mb-1">Travel, boarding & lodging</p>
              {tblEnabled ? (
                <>
                  <p className="text-emerald-500/90 text-[13px] font-medium mb-2">Applicable</p>
                  <textarea value={travelBoardingLodging} onChange={(e) => { setTravelBoardingLodging(e.target.value); setValidationError(null); }} placeholder="Enter requirements" className={cn(inputBaseClass, 'min-h-[100px] resize-y py-3')} rows={3} />
                </>
              ) : (
                <p className="text-[#8E8E93] text-sm">Not applicable</p>
              )}
            </div>
          </div>
        </section>

        {/* Bank account */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Bank account
            </h2>
            <p className="text-white/80 text-sm mt-1">Choose where to receive payment</p>
          </div>
          <div className="p-5 space-y-4">
            {banks.length === 0 ? (
              <div className="py-6 text-center rounded-2xl bg-amber-500/5 border border-amber-500/20">
                <p className="text-amber-500/90 text-sm font-medium mb-1">No bank accounts yet</p>
                <p className="text-[#8E8E93] text-[13px] mb-4">Add one in Event Essentials → Billing Details.</p>
                <Button type="button" variant="secondary" onClick={() => navigate('/profile/event-essentials?tab=billing')} className="min-h-[44px]">Go to Billing</Button>
              </div>
            ) : (
              <>
                <label className="block text-sm font-medium text-white">Account</label>
                <select
                  value={selectedBankId ?? ''}
                  onChange={(e) => { setSelectedBankId(e.target.value ? Number(e.target.value) : null); setValidationError(null); }}
                  className={inputBaseClass}
                >
                  <option value="">Choose account</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>{b.bank_name} · ****{b.account_number.slice(-4)}</option>
                  ))}
                </select>
                {selectedBankId != null && bankFieldsFromSelection && (
                  <div className="p-4 rounded-2xl bg-[#0B0B0D] border border-[#3A3A3C] space-y-2 text-[13px]">
                    <div className="flex justify-between"><span className="text-[#8E8E93]">Bank</span><span className="text-white font-medium">{bankFieldsFromSelection.bank_name}</span></div>
                    <div className="flex justify-between"><span className="text-[#8E8E93]">Branch Name</span><span className="text-white font-medium">{bankFieldsFromSelection.branch}</span></div>
                    <div className="flex justify-between"><span className="text-[#8E8E93]">Account Holder</span><span className="text-white font-medium">{bankFieldsFromSelection.account_holder_name}</span></div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* GST & PAN */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <Banknote className="h-4 w-4" /> GST & PAN
            </h2>
          </div>
          <div className="p-5 space-y-4">
            {!hasPan && (
              <div className="py-3 px-4 rounded-xl bg-amber-500/5 border border-amber-500/20 mb-2">
                <p className="text-amber-500/90 text-[13px]">Add PAN in Billing Details to reuse across bookings.</p>
                <button type="button" onClick={() => navigate('/profile/event-essentials?tab=billing')} className="text-[#F33363] text-[13px] font-medium mt-1">Go to Billing →</button>
              </div>
            )}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              <Input label="PAN (required)" value={pan} onChange={(e) => { setPan(e.target.value.toUpperCase()); setValidationError(null); }} placeholder="e.g. ABCDExxxxx" maxLength={10} className={inputBaseClass} />
              <Input label="GSTIN (optional)" value={gstin} onChange={(e) => { setGstin(e.target.value.toUpperCase()); setValidationError(null); }} placeholder="GSTIN" className={inputBaseClass} />

            </div>
          </div>
        </section>

        {/* Terms */}
        <section className="rounded-[2rem] bg-[#151517] border border-[#2E2E32] overflow-hidden">
          <div className="p-5 border-b border-[#2E2E32]">
            <h2 className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider flex items-center gap-2">
              <FileText className="h-4 w-4" /> Terms
            </h2>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-[#E5E5EA]/60 text-[13px] leading-relaxed">{TERMS_TEXT}</p>
            <button
              type="button"
              onClick={() => setAnnexureOpen(!annexureOpen)}
              className="flex items-center gap-2 text-[#F33363] font-medium text-[13px] min-h-[44px]"
            >
              {annexureOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Annexure A — Booking Terms and Conditions
            </button>
            {annexureOpen && (
              <div className="mt-3 p-4 rounded-2xl bg-[#0B0B0D] border border-[#3A3A3C] max-h-[50vh] overflow-y-auto">
                <AnnexureAContent />
              </div>
            )}
            <div className="pt-2">
              <Checkbox
                checked={termsAccepted}
                onCheckedChange={(c) => { setTermsAccepted(c === true); setValidationError(null); }}
                label="I accept the Booking Terms and Conditions (Annexure A) and confirm the above."
              />
            </div>
          </div>
        </section>

        <Button
          type="button"
          onClick={handleConfirmBookingClick}
          disabled={submitLoading}
          variant="active"
          className="w-full py-4 rounded-2xl font-bold text-base min-h-[52px] bg-[#F33363] hover:bg-[#FF5E8E] text-white shadow-lg active:scale-[0.99]"
        >
          {submitLoading ? <Loader className="h-5 w-5 animate-spin" /> : 'Confirm Booking'}
        </Button>
      </div>

      {/* Confirm modal */}
      {confirmModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="rounded-2xl border border-[#3A3A3C]/50 shadow-xl w-full max-w-sm overflow-hidden bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)]">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-bold text-white">Confirm booking?</h3>
              <p className="text-[#8E8E93] text-sm">You’re about to submit this form. This can’t be undone.</p>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="secondary" className="flex-1 min-h-[48px]" onClick={() => setConfirmModalOpen(false)} disabled={submitLoading}>Cancel</Button>
                <Button type="button" variant="active" className="flex-1 min-h-[48px] bg-[#F33363] hover:bg-[#FF5E8E]" onClick={() => void handleConfirmSubmit()} disabled={submitLoading}>
                  {submitLoading ? <Loader className="h-5 w-5 animate-spin" /> : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
